import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Assistir a gravação de uma ligação (card 3112).
 *
 * O vídeo vem de um **link temporário** do blob, pedido a cada abertura — por isso a modal não
 * guarda a URL entre aberturas e a zera ao fechar (o que também para o vídeo).
 *
 * Serve às duas portas: com `dealId` pede pela aba do deal (quem vê o deal vê a gravação);
 * sem, pela visão geral do time interno.
 */
@Component({
  selector: 'app-modal-meeting-recording',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './modal-meeting-recording.component.html',
  styleUrl: './modal-meeting-recording.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetingRecordingComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  protected readonly ligacao = signal<MeetingDto | null>(null);
  protected readonly url = signal<string | null>(null);
  protected readonly carregando = signal(false);
  protected readonly erro = signal<string | null>(null);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService
  ) {
    super(translateService);
  }

  /** Abre a modal e pede o link. `dealId` escolhe a porta da aba do deal. */
  open(meeting: MeetingDto, dealId: string | null = null): void {
    this.ligacao.set(meeting);
    this.url.set(null);
    this.erro.set(null);
    this.carregando.set(true);

    this.dialogRef?.open('', '', '', 'Fechar', '', 'meeting-recording', false, true);

    const pedido = dealId
      ? this.meetingsService.getDealMeetingRecordingUrl(dealId, meeting.id)
      : this.meetingsService.getRecordingUrl(meeting.id);

    pedido.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: resposta => {
        // Resposta atrasada de uma ligação aberta antes: não troca o vídeo da que está na tela.
        if (this.ligacao()?.id !== meeting.id) return;
        this.carregando.set(false);
        if (resposta?.url) this.url.set(resposta.url);
        else this.erro.set('A gravação desta reunião ainda não está disponível.');
      },
      error: erro => {
        if (this.ligacao()?.id !== meeting.id) return;
        this.carregando.set(false);
        this.erro.set(mensagemDoErro(erro, 'Não foi possível abrir a gravação.'));
      },
    });
  }

  /** Só leitura: não há formulário. Existe para seguir o contrato das modais do projeto. */
  isFormValid(): boolean {
    return true;
  }

  /** O único "envio" desta modal é fechar. */
  submitAction(): void {
    this.fechar();
  }

  /** O `<video>` recusou o arquivo — em geral o link expirou. */
  protected videoFalhou(): void {
    this.url.set(null);
    this.erro.set(
      'Não foi possível reproduzir a gravação. O link pode ter expirado: feche e abra de novo.'
    );
  }

  fechar(): void {
    this.dialogRef?.close();
  }

  /** Zera a URL ao fechar: tira o vídeo do DOM (para o áudio) e força link novo na próxima. */
  protected aoFechar(): void {
    this.url.set(null);
  }
}
